/**
 * Display helpers — date formatting, money, channel labels, relative time, etc.
 * Light wrappers around Intl APIs so we don't need a date library.
 */

export function formatRelative(date: string | Date | null | undefined): string {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;
  const now = Date.now();
  const ms = now - d.getTime();
  // Future timestamps render as "in 3h" rather than negative-second nonsense.
  if (ms < 0) return formatTimeUntil(d);
  const sec = Math.floor(ms / 1000);
  const min = Math.floor(sec / 60);
  const hr  = Math.floor(min / 60);
  const day = Math.floor(hr / 24);
  if (sec < 5)   return "just now";
  if (sec < 60)  return `${sec}s ago`;
  if (min < 60)  return `${min}m ago`;
  if (hr  < 24)  return `${hr}h ago`;
  if (day < 7)   return `${day}d ago`;
  if (day < 30)  return `${Math.floor(day / 7)}w ago`;
  if (day < 365) return formatDate(d, { month: "short", day: "numeric" });
  return formatDate(d, { year: "numeric", month: "short", day: "numeric" });
}

export function formatTimeUntil(date: string | Date | null | undefined): string {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;
  const ms = d.getTime() - Date.now();
  if (ms <= 0) return "now";
  const sec = Math.floor(ms / 1000);
  const min = Math.floor(sec / 60);
  const hr  = Math.floor(min / 60);
  const day = Math.floor(hr / 24);
  if (sec < 60)  return `in ${sec}s`;
  if (min < 60)  return `in ${min}m`;
  if (hr  < 24)  return `in ${hr}h`;
  if (day < 7)   return `in ${day}d`;
  if (day < 30)  return `in ${Math.floor(day / 7)}w`;
  return formatDate(d, { month: "short", day: "numeric" });
}

/**
 * The practice's timezone — set once at boot from the workspace settings.
 *
 * A med spa has ONE timezone: the one the doors open in. Rendering in the
 * browser's instead means a 10am visit reads as 11pm to anyone looking from
 * another country, and quietly misleads the owner the moment she travels.
 * Null until we've been told, in which case we fall back to the browser.
 */
let _displayTz: string | null = null;
export function setDisplayTimeZone(tz: string | null): void { _displayTz = tz || null; }
export function displayTimeZone(): string | null { return _displayTz; }

/**
 * The practice's booking grid in minutes (Settings → Spa scheduling). Open
 * times come off it server-side; dragging a visit on the calendar snaps to it
 * here, so a moved visit lands on 9:00 / 9:30 like everything else.
 */
let _gridMinutes = 30;
export function setBookingGridMinutes(n: unknown): void {
  if (typeof n === "number" && Number.isFinite(n) && n >= 5 && n <= 120) _gridMinutes = n;
}
export function bookingGridMinutes(): number { return _gridMinutes; }

/**
 * Format a plain calendar date ("2026-08-17") for display.
 *
 * Deliberately does NOT go through formatDate: a bare YYYY-MM-DD has no
 * timezone, so converting it into one shifts it. Rendering the practice's
 * 17 August in a zone nine hours behind produced "Sunday, August 16" beside
 * a date picker reading 2026-08-17.
 */
export function formatPlainDate(
  ymd: string,
  options: Intl.DateTimeFormatOptions = { weekday: "long", month: "long", day: "numeric" },
): string {
  const [y, m, d] = ymd.split("-").map(Number);
  if (!y || !m || !d) return ymd;
  // Anchored at UTC noon and formatted in UTC — no zone can move the day.
  return new Date(Date.UTC(y, m - 1, d, 12)).toLocaleDateString(undefined, { ...options, timeZone: "UTC" });
}

export function formatDate(date: string | Date | null | undefined, options?: Intl.DateTimeFormatOptions): string {
  if (!date) return "";
  // A bare YYYY-MM-DD is a day, not a moment: a birthday, not 00:00 anywhere.
  // Read as an instant it becomes midnight UTC, and midnight UTC shown in New
  // York is the evening before, which is how the 19th came out as the 18th.
  if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return formatPlainDate(date, options ?? { month: "short", day: "numeric", year: "numeric" });
  }
  const d = typeof date === "string" ? new Date(date) : date;
  const opts = options ?? { month: "short", day: "numeric", year: "numeric" };
  return d.toLocaleString(undefined, _displayTz ? { ...opts, timeZone: _displayTz } : opts);
}

/**
 * The title a document gets by default: what the file is, and when.
 *
 * "can we please have dates in the title for upload document / So the date is
 * automatically in there under title" - she was typing the date onto every lab
 * result by hand, and a scanner's "Scan0007.pdf" says nothing a year later.
 * The extension comes off because a title is read, not opened, and underscores
 * become spaces for the same reason. Still a plain text box she can type over.
 *
 * The date is the practice's day, not the browser's: formatDate goes through
 * the clinic timezone, so a file added at 9pm on the 21st doesn't file itself
 * under the 22nd.
 */
export function documentTitleFor(originalName: string, when: Date = new Date()): string {
  const base = originalName
    .replace(/\.[A-Za-z0-9]{1,8}$/, "")
    .replace(/[_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const day = formatDate(when, { month: "2-digit", day: "2-digit", year: "numeric" });
  return base ? `${base} - ${day}` : day;
}

export function formatTime(date: string | Date | null | undefined): string {
  return formatDate(date, { hour: "numeric", minute: "2-digit" });
}

/**
 * Format a Date as a YYYY-MM-DD string in the LOCAL timezone — never UTC.
 *
 * This is the right helper any time you're using a date string as a key
 * (calendar grid, day buckets, "today" comparisons, ?date= query params).
 * Using `d.toISOString().slice(0,10)` for this silently produces the UTC
 * date, so a 9pm event in PST gets bucketed onto the next day.
 */
export function formatLocalDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/* ─── Day maths in the PRACTICE's timezone ─────────────────────────────────
 *
 * formatLocalDateKey above answers in the BROWSER's timezone, which is right
 * for things the browser owns (a month grid the user clicks, an <input
 * type="date">). It is wrong for the day schedule: labels there render in the
 * practice's timezone, so bucketing and positioning must agree with them or
 * the two drift apart. Viewed from Tokyo, a New York practice's 9am visit
 * belongs to the New York day at the New York hour — not to whatever day it
 * happens to be in Tokyo.
 *
 * All three fall back to browser-local until setDisplayTimeZone() has been
 * called, and are exactly equal to the local versions whenever the viewer
 * already sits in the practice's timezone (i.e. her whole team).
 */

/** Offset of `tz` from UTC at a given instant, in ms. Positive = ahead. */
function tzOffsetMs(tz: string, at: Date): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
  }).formatToParts(at);
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? 0);
  const asUtc = Date.UTC(get("year"), get("month") - 1, get("day"), get("hour") % 24, get("minute"), get("second"));
  return asUtc - at.getTime();
}

/**
 * YYYY-MM-DD for the calendar day this instant falls on, practice-side.
 *
 * Built from formatToParts, not from an `en-CA` format string: that locale
 * yields 2026-08-20 on some ICU builds and 08/20/2026 on others, which is
 * invisible in one browser and breaks every date comparison in another. The
 * server hit exactly this and returned 500s for a week's worth of screens.
 */
export function displayDayKey(date: Date): string {
  if (!_displayTz) return formatLocalDateKey(date);
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: _displayTz, year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(date);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

/** The instant at which a practice day (YYYY-MM-DD) begins. */
export function displayDayStart(dayKey: string): Date {
  const [y, m, d] = dayKey.split("-").map(Number);
  if (!y || !m || !d) return new Date(NaN);
  if (!_displayTz) return new Date(y, m - 1, d);
  const naive = Date.UTC(y, m - 1, d);
  // Two passes so a day starting inside a DST shift lands on the right side.
  let offset = tzOffsetMs(_displayTz, new Date(naive));
  offset = tzOffsetMs(_displayTz, new Date(naive - offset));
  return new Date(naive - offset);
}

/** Minutes past midnight, practice-side — the y-axis of the day grid. */
export function displayMinutesOfDay(date: Date): number {
  if (!_displayTz) return date.getHours() * 60 + date.getMinutes();
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: _displayTz, hour: "2-digit", minute: "2-digit", hour12: false,
  }).formatToParts(date);
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? 0);
  return (get("hour") % 24) * 60 + get("minute");
}

export function formatDateTime(date: string | Date | null | undefined): string {
  return formatDate(date, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

/**
 * Format a cents-denominated amount as a currency string.
 *
 * Defaults to 2 decimal places — anything else silently corrupts invoice
 * totals (a $99.50 invoice rendered as "$100" is a real bug, not a style
 * choice). For dashboard tiles where you actually want the rounded
 * approximate, use formatMoneyApprox.
 */
export function formatMoney(cents: number, currency = "USD"): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(cents / 100);
  } catch {
    return `$${(cents / 100).toFixed(2)}`;
  }
}

/** Like formatMoney but rounds to whole units — for compact dashboard tiles. */
export function formatMoneyApprox(cents: number, currency = "USD"): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(cents / 100);
  } catch {
    return `$${(cents / 100).toFixed(0)}`;
  }
}

export function formatPhone(phone: string | null): string {
  if (!phone) return "";
  // Display as +1 (415) 555-1234 or fallback to original
  const digits = phone.replace(/[^\d+]/g, "");
  if (/^\+1\d{10}$/.test(digits)) {
    return `+1 (${digits.slice(2, 5)}) ${digits.slice(5, 8)}-${digits.slice(8)}`;
  }
  return phone;
}

// Channel labels live in lib/channels.ts (single source of truth across
// the inbox filter pills, consent panel, channel-icon picker, message
// provenance, etc.). Re-exported here for backwards compatibility with
// existing `from "@/lib/format"` imports.
export { CHANNEL_LABELS, channelLabel } from "./channels";

export function getInitials(name: string | null | undefined): string {
  if (!name) return "??";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

export function fullName(c: { first_name?: string | null; last_name?: string | null; full_name?: string | null; email?: string | null } | null | undefined): string {
  if (!c) return "Unknown";
  if (c.full_name) return c.full_name;
  const n = [c.first_name, c.last_name].filter(Boolean).join(" ");
  return n || c.email || "Unknown";
}
